<template>
  <div class="dynamic-course-page">
    <!-- Animated Background Icons -->
    <div class="animated-bg-icons">
      <!-- Top Left Corner Cluster -->
      <i class="fas fa-graduation-cap bg-icon icon-1"></i>
      <i class="fas fa-book-open bg-icon icon-2"></i>
      <i class="fas fa-brain bg-icon icon-3"></i>
      <i class="fas fa-lightbulb bg-icon icon-4"></i>
      <i class="fas fa-code bg-icon icon-5"></i>

      <!-- Top Right Corner Cluster -->
      <i class="fas fa-robot bg-icon icon-6"></i>
      <i class="fas fa-rocket bg-icon icon-7"></i>
      <i class="fas fa-microchip bg-icon icon-8"></i>
      <i class="fas fa-atom bg-icon icon-9"></i>
      <i class="fas fa-star bg-icon icon-10"></i>

      <!-- Bottom Left Corner Cluster -->
      <i class="fas fa-database bg-icon icon-11"></i>
      <i class="fas fa-network-wired bg-icon icon-12"></i>
      <i class="fas fa-laptop-code bg-icon icon-13"></i>
      <i class="fas fa-certificate bg-icon icon-14"></i>
      <i class="fas fa-cloud bg-icon icon-15"></i>

      <!-- Bottom Right Corner Cluster -->
      <i class="fas fa-cogs bg-icon icon-16"></i>
      <i class="fas fa-chart-line bg-icon icon-17"></i>
      <i class="fas fa-infinity bg-icon icon-18"></i>
      <i class="fas fa-user-graduate bg-icon icon-19"></i>
      <i class="fas fa-chalkboard-teacher bg-icon icon-20"></i>

      <!-- Center Scattered Icons -->
      <i class="fas fa-flask bg-icon icon-21"></i>
      <i class="fas fa-puzzle-piece bg-icon icon-22"></i>
      <i class="fas fa-magic bg-icon icon-23"></i>
      <i class="fas fa-trophy bg-icon icon-24"></i>
      <i class="fas fa-bullseye bg-icon icon-25"></i>
      <i class="fas fa-fire bg-icon icon-26"></i>
      <i class="fas fa-bolt bg-icon icon-27"></i>
      <i class="fas fa-cubes bg-icon icon-28"></i>
      <i class="fas fa-project-diagram bg-icon icon-29"></i>
      <i class="fas fa-palette bg-icon icon-30"></i>

      <!-- Additional Edge Icons -->
      <i class="fas fa-layer-group bg-icon icon-31"></i>
      <i class="fas fa-gem bg-icon icon-32"></i>
      <i class="fas fa-compass bg-icon icon-33"></i>
      <i class="fas fa-shield-alt bg-icon icon-34"></i>
      <i class="fas fa-crown bg-icon icon-35"></i>
      <i class="fas fa-key bg-icon icon-36"></i>
      <i class="fas fa-feather bg-icon icon-37"></i>
      <i class="fas fa-bookmark bg-icon icon-38"></i>
      <i class="fas fa-award bg-icon icon-39"></i>
      <i class="fas fa-medal bg-icon icon-40"></i>
    </div>

    <!-- AI News Ticker - ALWAYS VISIBLE (outside conditional rendering) -->
    <AINewsTickerFloating />

    <!-- Loading State - Ultra Modern Design -->
    <div v-if="loading" class="loading-container">
      <!-- Gradient Background -->
      <div class="loader-gradient-bg"></div>

      <!-- Animated Blob Background -->
      <div class="blob-container">
        <div class="blob blob-1"></div>
        <div class="blob blob-2"></div>
        <div class="blob blob-3"></div>
      </div>

      <!-- Main Content -->
      <div class="loader-main">
        <!-- Logo Section -->
        <div class="logo-wrapper">
          <!-- Outer Glow -->
          <div class="logo-outer-glow"></div>

          <!-- Spinning Arc -->
          <svg class="spinner-arc" viewBox="0 0 100 100">
            <defs>
              <linearGradient id="vueArcGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#8b5cf6"/>
                <stop offset="50%" stop-color="#06b6d4"/>
                <stop offset="100%" stop-color="#8b5cf6"/>
              </linearGradient>
            </defs>
            <circle class="arc-track" cx="50" cy="50" r="46"/>
            <circle class="arc-progress" cx="50" cy="50" r="46"/>
          </svg>

          <!-- Logo Circle -->
          <div class="logo-circle">
            <img src="https://imagedelivery.net/GSYWs1kbpG8XypJe3mjC0Q/45443cd4-6dcd-4293-cb53-5fb149065400/public" alt="Bheem Academy" class="logo-img">
          </div>

          <!-- Pulse Rings -->
          <div class="pulse-ring pulse-1"></div>
          <div class="pulse-ring pulse-2"></div>
          <div class="pulse-ring pulse-3"></div>
        </div>

        <!-- Text Content -->
        <div class="loader-text">
          <h1 class="loader-title">Bheem Academy</h1>
          <p class="loader-tagline">AI-Powered Learning Platform</p>
        </div>

        <!-- Progress Section -->
        <div class="loader-progress">
          <div class="progress-info">
            <span class="progress-status">{{ loadingMessage }}</span>
            <span class="progress-dots"><span>.</span><span>.</span><span>.</span></span>
          </div>
          <div class="progress-bar-wrapper">
            <div class="progress-bar-track">
              <div class="progress-bar-fill"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Floating Elements -->
      <div class="floating-elements">
        <div class="float-icon fi-1"><i class="fas fa-brain"></i></div>
        <div class="float-icon fi-2"><i class="fas fa-robot"></i></div>
        <div class="float-icon fi-3"><i class="fas fa-code"></i></div>
        <div class="float-icon fi-4"><i class="fas fa-graduation-cap"></i></div>
        <div class="float-icon fi-5"><i class="fas fa-lightbulb"></i></div>
        <div class="float-icon fi-6"><i class="fas fa-rocket"></i></div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <i class="fas fa-exclamation-triangle"></i>
      <h2>Error Loading Course</h2>
      <p>{{ error }}</p>
      <button @click="retryLoading" class="retry-btn">
        <i class="fas fa-redo"></i> Try Again
      </button>
    </div>

    <!-- Main Content -->
    <div v-if="courseData" class="course-content">

      <!-- Hero Section - Dark Card Design -->
      <section class="course-hero">
        <div class="hero-content-box">
          <div class="hero-content-grid">
            <!-- Left: Text Content -->
            <div class="hero-text">
              <div class="hero-badge">
                <i class="fas fa-bolt"></i>
                <span>{{ metadata.difficulty }} Level</span>
              </div>

              <h1 class="hero-title">{{ courseData.fullname }}</h1>

              <p class="hero-subtitle" v-html="getCourseSummaryText()"></p>

              <div class="hero-stats">
                <div class="hero-stat-item">
                  <div class="hero-stat-value">{{ metadata.duration?.split(' ')[0] || '2' }}</div>
                  <div class="hero-stat-label">{{ metadata.duration?.split(' ')[1] || 'Months' }}</div>
                </div>
                <div class="hero-stat-item">
                  <div class="hero-stat-value">{{ metadata.enrolled }}+</div>
                  <div class="hero-stat-label">Enrolled</div>
                </div>
                <!-- IBM Certificate for Youth, Superstar, Kodee courses only -->
                <div v-if="showIBMCertificate" class="hero-stat-item">
                  <div class="hero-stat-value"><img src="https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg" alt="IBM" style="height: 24px; width: auto; filter: brightness(0) invert(1);" /></div>
                  <div class="hero-stat-label">Certification</div>
                </div>
              </div>

              <div class="hero-buttons">
                <button class="hero-btn hero-btn-primary" @click="handleEnroll">
                  <i class="fas fa-rocket"></i>
                  Enroll Now
                </button>
              </div>
            </div>

            <!-- Right: Visual/Image -->
            <div class="hero-visual">
              <!-- Floating Cards -->
              <div class="hero-floating hero-floating-1">
                <i class="fas fa-wand-magic-sparkles"></i>
                <span>AI-Powered</span>
              </div>
              <div class="hero-floating hero-floating-2">
                <i class="fas fa-graduation-cap"></i>
                <span>{{ metadata.difficulty }}</span>
              </div>

              <!-- Course Image -->
              <div class="hero-image-container">
                <img :src="heroImage" :alt="courseData.fullname" class="hero-course-image" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Main Three-Column Grid -->
      <div class="course-grid">
        <!-- Left Sidebar (showing Right content) -->
        <aside class="left-sidebar">
          <!-- Professional CTA Card - 3D Modern Design -->
          <div class="glass-card course-details-card">
            <div class="card-header gradient-header">
              <i class="fas fa-info-circle"></i>
              <span>Course Details</span>
            </div>
            <div class="card-body">
              <div class="detail-item">
                <div class="detail-icon">
                  <i class="fas fa-language"></i>
                </div>
                <div class="detail-content">
                  <span class="detail-label">Language</span>
                  <span class="detail-value">English & Malayalam</span>
                </div>
              </div>
              <div class="detail-item">
                <div class="detail-icon">
                  <i class="fas fa-certificate"></i>
                </div>
                <div class="detail-content">
                  <span class="detail-label">Certificate</span>
                  <span class="detail-value">Included</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Platform Features Tabbed Card -->
          <div class="glass-card platform-features-card">
            <div class="card-header gradient-header elegant-shine">
              <i class="fas fa-sparkles"></i>
              <span class="elegant-title">What We Offer</span>
            </div>
            <div class="card-body">
              <!-- Tab Navigation -->
              <div class="feature-tabs">
                <button
                  v-for="feature in platformFeatures"
                  :key="feature.id"
                  :class="['feature-tab', { active: activeFeatureTab === feature.id }]"
                  @click="activeFeatureTab = feature.id"
                >
                  <i :class="feature.icon"></i>
                  <span>{{ feature.title }}</span>
                </button>
              </div>

              <!-- Tab Content Panel -->
              <div class="feature-content-panel">
                <div
                  v-for="feature in platformFeatures"
                  :key="feature.id"
                  v-show="activeFeatureTab === feature.id"
                  :data-feature="feature.id"
                  class="feature-panel"
                >
                  <div class="feature-panel-icon">
                    <i :class="feature.icon"></i>
                  </div>
                  <h4 class="feature-panel-title">{{ feature.title }}</h4>
                  <p class="feature-panel-subtitle">{{ feature.subtitle }}</p>
                  <p class="feature-panel-description">{{ feature.description }}</p>
                  <ul class="feature-panel-list">
                    <li v-for="(item, index) in feature.features" :key="index">
                      <i class="fas fa-check-circle"></i>
                      <span>{{ item }}</span>
                    </li>
                  </ul>
                  <a
                    :href="feature.link"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="feature-panel-btn"
                  >
                    <span>{{ feature.buttonText }}</span>
                    <i class="fas fa-arrow-right"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <!-- Mentorship Quality Card -->
          <div class="glass-card mentorship-card">
            <div class="card-header gradient-header">
              <i class="fas fa-users-cog"></i>
              <span>Quality Mentorship</span>
            </div>
            <div class="card-body">
              <div class="mentorship-highlights">
                <div class="mentorship-item">
                  <div class="mentorship-icon">
                    <i class="fas fa-user-graduate"></i>
                  </div>
                  <div class="mentorship-content">
                    <h5>Expert Guidance</h5>
                    <p>Learn from industry professionals with years of real-world experience</p>
                  </div>
                </div>

                <div class="mentorship-item">
                  <div class="mentorship-icon">
                    <i class="fas fa-comments"></i>
                  </div>
                  <div class="mentorship-content">
                    <h5>Personalized Support</h5>
                    <p>Get individual attention and feedback tailored to your learning style</p>
                  </div>
                </div>

                <div class="mentorship-item">
                  <div class="mentorship-icon">
                    <i class="fas fa-clock"></i>
                  </div>
                  <div class="mentorship-content">
                    <h5>24/7 Availability</h5>
                    <p>Access mentor support whenever you need help or have questions</p>
                  </div>
                </div>

                <div class="mentorship-item">
                  <div class="mentorship-icon">
                    <i class="fas fa-chart-line"></i>
                  </div>
                  <div class="mentorship-content">
                    <h5>Progress Tracking</h5>
                    <p>Regular check-ins to ensure you're on track to meet your goals</p>
                  </div>
                </div>

                <div class="mentorship-item">
                  <div class="mentorship-icon">
                    <i class="fas fa-hands-helping"></i>
                  </div>
                  <div class="mentorship-content">
                    <h5>Career Guidance</h5>
                    <p>Get insights on career paths and opportunities in your field</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </aside>

        <!-- Center Content -->
        <main class="center-content">
          <!-- Tab Navigation Bar -->
          <div class="course-tab-bar">
            <div class="tab-bar-header">
              <i class="fas fa-compass"></i>
              <span>Explore Course</span>
            </div>
            <div class="tabs-container">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                :class="['tab-chip', { active: activeTab === tab.id, locked: tab.locked }]"
                @click="!tab.locked && (activeTab = tab.id)"
              >
                <i :class="tab.icon"></i>
                <div class="tab-text">
                  <span class="tab-title">{{ tab.title }}</span>
                </div>
                <i v-if="tab.locked" class="fas fa-lock lock-icon"></i>
              </button>
            </div>
          </div>

          <!-- Tab Content Panels -->
          <div class="tab-panels-container">
            <!-- Overview Tab -->
            <div v-show="activeTab === 'overview'" class="tab-panel fade-in">
              <!-- Vertical Sections (NEW - No Tabs) -->
              <VerticalSections
                v-if="getTabVerticalSections('overview')"
                :sections="getTabVerticalSections('overview')"
              />

              <!-- Nested Tabs Structure (FALLBACK) -->
              <NestedTabs
                v-else-if="getTabNestedTabs('overview')"
                :tabs="getTabNestedTabs('overview').tabs"
                :defaultTab="getTabNestedTabs('overview').defaultTab"
              />

              <!-- Modern Structure if sections are available -->
              <ModernTabPanel
                v-else-if="getTabSections('overview')"
                :sections="getTabSections('overview')"
              />

              <!-- Legacy Content if available -->
              <div v-else-if="getTabContent('overview')" class="custom-tab-section">
                <h2 v-if="getTabContent('overview').title" class="panel-heading">
                  <i class="fas fa-lightbulb"></i>
                  {{ getTabContent('overview').title }}
                </h2>
                <p v-if="getTabContent('overview').subtitle" class="panel-subtitle">
                  {{ getTabContent('overview').subtitle }}
                </p>
                <div class="custom-tab-content" v-html="getTabContent('overview').content"></div>
              </div>

              <!-- Default Content -->
              <div v-else>
                <h2 class="panel-heading">
                  <i class="fas fa-lightbulb"></i>
                  Learning Objectives
                </h2>
                <div class="objectives-grid">
                  <div v-for="(objective, index) in learningObjectives" :key="index" class="objective-tile">
                    <div class="tile-icon">
                      <i :class="objective.icon"></i>
                    </div>
                    <h3 class="tile-title">{{ objective.title }}</h3>
                    <p class="tile-description">{{ objective.description }}</p>
                    <div class="tile-glow"></div>
                  </div>
                </div>
                <div class="overview-content" v-html="courseData.summary"></div>
              </div>
            </div>

            <!-- Curriculum Tab -->
            <div v-show="activeTab === 'curriculum'" class="tab-panel fade-in">
              <h2 class="panel-heading">
                <i class="fas fa-book"></i>
                Course Curriculum
              </h2>
              <p class="panel-subtitle">Explore the complete course structure and learning path</p>

              <!-- Custom Curriculum from tab_config -->
              <div v-if="getTabCurriculum('curriculum')" class="curriculum-accordion-modern">
                <div
                  v-for="(module, index) in getTabCurriculum('curriculum')"
                  :key="module.number"
                  :class="['module-item-modern', { expanded: curriculumExpanded[index] }]"
                >
                  <div class="module-header-modern" @click="toggleCurriculumModule(index)">
                    <div class="module-left">
                      <div class="module-number-modern">{{ module.number }}</div>
                      <div class="module-info-modern">
                        <h3 class="module-title-modern">{{ module.title }}</h3>
                        <div class="module-stats" v-if="(module.duration && module.duration !== '0 minutes' && module.duration !== '0 min') || (module.lessons && module.lessons.length > 0)">
                          <span class="stat-badge" v-if="module.duration && module.duration !== '0 minutes' && module.duration !== '0 min' && !module.duration.startsWith('0 ')" >
                            <i class="fas fa-clock"></i>
                            {{ module.duration }}
                          </span>
                          <span class="stat-badge type-badge" v-if="module.type && module.type !== 'Core'">
                            <i class="fas fa-tag"></i>
                            {{ module.type }}
                          </span>
                          <span class="stat-badge" v-if="module.lessons && module.lessons.length > 0">
                            <i class="fas fa-list"></i>
                            {{ module.lessons.length }} Lessons
                          </span>
                        </div>
                      </div>
                    </div>
                    <i :class="['fas', curriculumExpanded[index] ? 'fa-chevron-up' : 'fa-chevron-down', 'expand-icon-modern']"></i>
                  </div>

                  <transition name="accordion-expand">
                    <div v-if="curriculumExpanded[index]" class="module-body-modern">
                      <div v-if="module.description" class="module-description-modern">{{ module.description }}</div>

                      <div class="lessons-grid-modern">
                        <div
                          v-for="(lesson, lessonIndex) in module.lessons"
                          :key="lessonIndex"
                          class="lesson-card-modern"
                        >
                          <div class="lesson-icon-modern">
                            <i :class="getLessonIcon(lesson.type)"></i>
                          </div>
                          <div class="lesson-content-modern">
                            <h4 class="lesson-title-modern">{{ lesson.title }}</h4>
                            <span class="lesson-type-modern">{{ getLessonTypeName(lesson.type) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>

              <!-- Default Curriculum from Moodle sections -->
              <div v-else class="curriculum-accordion-modern">
                <div
                  v-for="(section, index) in sections"
                  :key="section.id"
                  :class="['module-item-modern', { expanded: section.expanded }]"
                >
                  <div class="module-header-modern" @click="toggleSection(index)">
                    <div class="module-left">
                      <div class="module-number-modern">{{ String(index + 1).padStart(2, '0') }}</div>
                      <div class="module-info-modern">
                        <h3 class="module-title-modern">{{ section.name }}</h3>
                        <div class="module-stats" v-if="section.activities && section.activities.length > 0">
                          <span class="stat-badge" v-if="countByType(section.activities, ['page', 'lesson', 'video']) > 0">
                            <i class="fas fa-play-circle"></i>
                            {{ countByType(section.activities, ['page', 'lesson', 'video']) }} Lessons
                          </span>
                          <span class="stat-badge" v-if="countByType(section.activities, ['quiz']) > 0">
                            <i class="fas fa-question-circle"></i>
                            {{ countByType(section.activities, ['quiz']) }} Quizzes
                          </span>
                          <span class="stat-badge" v-if="countByType(section.activities, ['assign']) > 0">
                            <i class="fas fa-clipboard-check"></i>
                            {{ countByType(section.activities, ['assign']) }} Assignments
                          </span>
                        </div>
                      </div>
                    </div>
                    <i :class="['fas', section.expanded ? 'fa-chevron-up' : 'fa-chevron-down', 'expand-icon-modern']"></i>
                  </div>

                  <transition name="accordion-expand">
                    <div v-if="section.expanded" class="module-body-modern">
                      <div v-if="section.summary" class="module-description-modern" v-html="section.summary"></div>

                      <div class="lessons-grid-modern">
                        <div
                          v-for="activity in section.activities"
                          :key="activity.id"
                          class="lesson-card-modern"
                        >
                          <div class="lesson-icon-modern">
                            <i :class="getActivityIcon(activity.modname)"></i>
                          </div>
                          <div class="lesson-content-modern">
                            <h4 class="lesson-title-modern">{{ activity.name }}</h4>
                            <span class="lesson-type-modern">{{ getActivityTypeName(activity.modname) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>

            <!-- Projects Tab -->
            <div v-show="activeTab === 'projects'" class="tab-panel fade-in">
              <!-- Project Cards Grid -->
              <ProjectCardsGrid
                v-if="getTabProjects('projects')"
                :projects="getTabProjects('projects')"
                @project-click="handleProjectClick"
              />
              <!-- Projects with Nested Tabs (legacy) -->
              <NestedTabs
                v-else-if="getTabNestedTabs('projects')"
                :tabs="getTabNestedTabs('projects').tabs"
                :defaultTab="getTabNestedTabs('projects').defaultTab"
              />
              <!-- Modern Structure (legacy) -->
              <ModernTabPanel
                v-else-if="getTabSections('projects')"
                :sections="getTabSections('projects')"
              />
              <!-- Legacy Content -->
              <div v-else-if="getTabContent('projects')">
                <h2 v-if="getTabContent('projects').title" class="panel-heading">
                  <i class="fas fa-project-diagram"></i>
                  {{ getTabContent('projects').title }}
                </h2>
                <p v-if="getTabContent('projects').subtitle" class="panel-subtitle">
                  {{ getTabContent('projects').subtitle }}
                </p>
                <div class="custom-tab-content" v-html="getTabContent('projects').content"></div>
              </div>
              <!-- Default Content -->
              <div v-else>
                <h2 class="panel-heading">
                  <i class="fas fa-project-diagram"></i>
                  Hands-On Projects
                </h2>
                <p class="coming-soon-text">Project content coming soon...</p>
              </div>
            </div>

            <!-- Reviews Tab -->
            <div v-show="activeTab === 'reviews'" class="tab-panel fade-in">
              <!-- Google Reviews Panel (NEW - Live Data) -->
              <GoogleReviewsPanel :courseId="props.courseId" />
            </div>

            <!-- FAQ Tab -->
            <div v-show="activeTab === 'faq'" class="tab-panel fade-in">
              <FAQPanel
                :courseName="courseData?.fullname || 'this course'"
                :courseId="props.courseId"
                :faqs="getTabFAQs('faq')"
              />
            </div>
          </div>
        </main>

        <!-- Right Sidebar (showing Left content) -->
        <aside class="right-sidebar">
          <!-- Why Choose This Course Card - Matching Left Sidebar Style -->
          <div class="glass-card why-choose-card">
            <div class="card-header gradient-header">
              <i class="fas fa-star"></i>
              <span>Why Choose This Course</span>
            </div>
            <div class="card-body">
              <div
                v-for="(highlight, index) in sidebarKeyHighlights.slice(0, 6)"
                :key="index"
                class="detail-item"
              >
                <div class="detail-icon">
                  <i :class="highlight.icon"></i>
                </div>
                <div class="detail-content">
                  <span class="detail-label">{{ highlight.title }}</span>
                  <span v-if="highlight.description" class="detail-value">{{ highlight.description }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Gamification Card -->
          <div class="glass-card gamification-card">
            <div class="card-header gradient-header">
              <i class="fas fa-trophy"></i>
              <span>Your Recommended Courses</span>
            </div>
            <div class="card-body">
              <!-- Modern Level Display -->
              <div class="level-display-box">
                <div class="level-glow-orb"></div>
                <div class="level-content">
                  <div class="level-header">
                    <span class="level-label">Current Level</span>
                    <span class="level-badge">
                      <i class="fas fa-bolt"></i> Active
                    </span>
                  </div>
                  <div class="level-showcase">
                    <div class="level-number-container">
                      <div class="level-number-3d">{{ metadata.level }}</div>
                      <div class="level-ring"></div>
                    </div>
                    <div class="level-info">
                      <div class="level-title-text">{{ metadata.levelTitle }}</div>
                      <div class="level-stats">
                        <div class="stat-item">
                          <i class="fas fa-star"></i>
                          <span>{{ progress.xp_points }} XP</span>
                        </div>
                        <div class="stat-divider"></div>
                        <div class="stat-item">
                          <i class="fas fa-target"></i>
                          <span>{{ nextLevelXP }} XP</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="xp-progress-container">
                    <div class="xp-progress-header">
                      <span class="xp-label">Progress to Next Level</span>
                      <span class="xp-percentage">{{ xpProgressPercentage }}%</span>
                    </div>
                    <div class="xp-progress-bar">
                      <div class="xp-fill" :style="{ width: xpProgressPercentage + '%' }">
                        <div class="xp-shimmer"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Modern Achievement Badges -->
              <div class="achievements-section">
                <div class="achievements-header">
                  <h4 class="achievements-title">
                    <i class="fas fa-award"></i>
                    Milestones
                  </h4>
                  <span class="achievements-count">{{ metadata.level }}/4</span>
                </div>
                <div class="achievements-badge-grid">
                  <div :class="['achievement-badge', metadata.level >= 1 ? 'unlocked' : 'locked']">
                    <div class="badge-glow"></div>
                    <div class="badge-icon">
                      <i class="fas fa-palette"></i>
                    </div>
                    <div class="badge-info">
                      <div class="badge-name">Beginner</div>
                      <div class="badge-level">Level 1</div>
                    </div>
                    <div v-if="metadata.level >= 1" class="badge-check">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>
                  <div :class="['achievement-badge', metadata.level >= 2 ? 'unlocked' : 'locked']">
                    <div class="badge-glow"></div>
                    <div class="badge-icon">
                      <i class="fas fa-graduation-cap"></i>
                    </div>
                    <div class="badge-info">
                      <div class="badge-name">Intermediate</div>
                      <div class="badge-level">Level 2</div>
                    </div>
                    <div v-if="metadata.level >= 2" class="badge-check">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>
                  <div :class="['achievement-badge', metadata.level >= 3 ? 'unlocked' : 'locked']">
                    <div class="badge-glow"></div>
                    <div class="badge-icon">
                      <i class="fas fa-briefcase"></i>
                    </div>
                    <div class="badge-info">
                      <div class="badge-name">Professional</div>
                      <div class="badge-level">Level 3</div>
                    </div>
                    <div v-if="metadata.level >= 3" class="badge-check">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>
                  <div :class="['achievement-badge', metadata.level >= 4 ? 'unlocked' : 'locked']">
                    <div class="badge-glow"></div>
                    <div class="badge-icon">
                      <i class="fas fa-star"></i>
                    </div>
                    <div class="badge-info">
                      <div class="badge-name">Expert</div>
                      <div class="badge-level">Level 4</div>
                    </div>
                    <div v-if="metadata.level >= 4" class="badge-check">
                      <i class="fas fa-check"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useCourseAPI } from '../../composables/useCourseAPI'
import type { CourseData, CourseSection, CourseProgress } from '../../composables/useCourseAPI'
import ModernTabPanel from '../tabpanels/ModernTabPanel.vue'
import NestedTabs from '../tabpanels/NestedTabs.vue'
import VerticalSections from '../tabpanels/VerticalSections.vue'
import ProjectTabContent from '../tabpanels/ProjectTabContent.vue'
import ProjectCardsGrid from '../tabpanels/ProjectCardsGrid.vue'
import ScheduleTimeline from '../tabpanels/ScheduleTimeline.vue'
import GoogleReviewsPanel from '../tabpanels/GoogleReviewsPanel.vue'
import FAQPanel from '../tabpanels/FAQPanel.vue'
import AINewsTickerFloating from '../news/AINewsTickerFloating.vue'
// Props
const props = defineProps<{
  courseId: number
}>()

// Composables
const {
  loading,
  error,
  fetchCourse,
  fetchCourseSections,
  getCourseMetadata,
  calculateProgress
} = useCourseAPI()

// State
const courseData = ref<CourseData | null>(null)
const sections = ref<(CourseSection & { expanded: boolean; progress?: number; status?: string })[]>([])
const metadata = ref({
  type: 'professional',
  level: 2,
  levelTitle: 'Youth Course - Level 2',
  duration: '6 Months',
  ageRange: '13-17 years',
  difficulty: 'Intermediate',
  price: '₹10,000',
  originalPrice: '₹15,000',
  enrolled: '500'
})
const progress = ref<CourseProgress>({
  overall: 35,
  assignments: { done: 2, total: 5, percentage: 40 },
  quizzes: { done: 3, total: 6, percentage: 50 },
  videos: { done: 4, total: 10, percentage: 40 },
  xp_points: 1250,
  current_level: 2
})

const activeTab = ref('overview')

// Reviews availability check
const hasReviews = ref(false)

// Curriculum data from API (mdl_course_curriculum table)
const curriculumModules = ref<any[]>([])

// Curriculum expanded state (for custom curriculum from tab_config)
const curriculumExpanded = ref<boolean[]>([])

// Loading state messages
const loadingMessages = [
  'Preparing your learning journey',
  'Loading course content',
  'Fetching AI modules',
  'Setting up your workspace',
  'Almost there'
]
const loadingMessage = ref(loadingMessages[0])
let loadingMessageInterval: number | null = null

// Platform Features Tabs
const activeFeatureTab = ref('create')
const platformFeatures = ref([
  {
    id: 'create',
    title: 'Create',
    subtitle: 'AI Workflow Builder',
    icon: 'fas fa-magic',
    description: 'Design custom AI workflows with drag-and-drop simplicity. Build automation without coding.',
    features: [
      'Drag & Drop Interface',
      '100+ Pre-built Templates',
      'API Integrations',
      'Real-time Testing'
    ],
    link: 'https://flow.bheem.cloud/',
    buttonText: 'Start Building'
  },
  {
    id: 'promote',
    title: 'Promote',
    subtitle: 'AI Marketing Hub',
    icon: 'fas fa-bullhorn',
    description: 'Amplify your brand with AI-driven content and viral marketing strategies.',
    features: [
      'Content Generation',
      'Social Media Tools',
      'Campaign Analytics',
      'Trend Insights'
    ],
    link: 'https://aibuzzcentral.bheem.co.uk/',
    buttonText: 'Boost Your Reach'
  },
  {
    id: 'manage',
    title: 'Manage',
    subtitle: 'Cloud Platform',
    icon: 'fas fa-cogs',
    description: 'Centralize your business operations. Manage courses, students, and analytics.',
    features: [
      'Course Management',
      'Student Dashboard',
      'Analytics & Reports',
      'Payment Processing'
    ],
    link: 'https://bheem.cloud/',
    buttonText: 'Take Control'
  },
  {
    id: 'earn',
    title: 'Earn',
    subtitle: 'Marketplace',
    icon: 'fas fa-hand-holding-usd',
    description: 'Sell AI services, courses, and digital products in our marketplace.',
    features: [
      'List Your Services',
      'Digital Products',
      'Secure Payments',
      'Global Audience'
    ],
    link: 'https://marketplace.bheem.co.uk/',
    buttonText: 'Start Earning'
  }
])

// Set hero image based on course ID
const getHeroImage = () => {
  // Kids courses (Bheem Junior series)
  const kidsCourses = [8, 9, 10, 11, 12, 13]
  if (kidsCourses.includes(props.courseId)) {
    return 'https://imagedelivery.net/GSYWs1kbpG8XypJe3mjC0Q/18da6ce1-7c63-4896-f40c-b8293ea06d00/public'
  }

  // Superstar courses (Bheem Superstar series)
  const superstarCourses = [38, 40, 54]
  if (superstarCourses.includes(props.courseId)) {
    return 'https://imagedelivery.net/GSYWs1kbpG8XypJe3mjC0Q/9c7c8917-3c8b-44d5-fd27-3395ea16ff00/public'
  }

  // Professional courses
  const professionalCourses = [15, 16, 17, 20, 35, 36, 37, 41, 42, 43, 46]
  if (professionalCourses.includes(props.courseId)) {
    return 'https://imagedelivery.net/GSYWs1kbpG8XypJe3mjC0Q/aeeb929c-940e-4f44-7ebd-e6b16c585c00/public'
  }

  // Youth course
  if (props.courseId === 39) {
    return 'https://imagedelivery.net/GSYWs1kbpG8XypJe3mjC0Q/2c147c83-9f8b-442c-4d41-4cf4be39e400/public'
  }

  // Kodee courses (63, 64, 65)
  const kodeeCourses = [63, 64, 65]
  if (kodeeCourses.includes(props.courseId)) {
    return 'https://imagedelivery.net/GSYWs1kbpG8XypJe3mjC0Q/2eefe639-9471-4bb4-e57b-344140204400/public'
  }

  // Default image
  return 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=400&fit=crop'
}

const heroImage = ref(getHeroImage())

// Check if course is a Kids/Junior course (no certificate shown)
const isKidsCourse = computed(() => {
  const kidsCourses = [8, 9, 10, 11, 12, 13] // Bheem Junior series (AI Basics and Mistery, etc.)
  return kidsCourses.includes(props.courseId)
})

// Check if course should show IBM Certificate (Youth, Superstar, and Kodee courses)
const showIBMCertificate = computed(() => {
  const youthCourses = [39] // Youth course
  const superstarCourses = [38, 40, 54] // Bheem Superstar series
  const kodeeCourses = [63, 64, 65] // Kodee Agentic Builder, Digital Promoter, CloudOps
  const ibmCertifiedCourses = [...youthCourses, ...superstarCourses, ...kodeeCourses]
  return ibmCertifiedCourses.includes(props.courseId)
})

// All available tabs - reviews tab visibility depends on hasReviews
const allTabs = [
  { id: 'overview', title: 'Overview', subtitle: "What you'll learn", icon: 'fas fa-book-open', locked: false, visible: true, order: 1 },
  { id: 'curriculum', title: 'Curriculum', subtitle: 'Course modules', icon: 'fas fa-list', locked: false, visible: true, order: 2 },
  { id: 'projects', title: 'Projects', subtitle: 'Build & create', icon: 'fas fa-project-diagram', locked: false, visible: true, order: 3 },
  { id: 'reviews', title: 'Reviews', subtitle: 'Feedback', icon: 'fas fa-star', locked: false, visible: true, order: 4 },
  { id: 'faq', title: 'FAQ', subtitle: 'Get answers', icon: 'fas fa-question-circle', locked: false, visible: true, order: 5 }
]

// Computed tabs - filters out reviews tab when no reviews exist
const tabs = computed(() => {
  return allTabs.filter(tab => {
    // Hide reviews tab if no reviews exist
    if (tab.id === 'reviews' && !hasReviews.value) {
      return false
    }
    return true
  })
})

// Get tab content for fixed tabs (legacy support)
const getTabContent = (tabId: string) => {
  if (courseData.value?.tab_config && courseData.value.tab_config[tabId]) {
    const tabData = courseData.value.tab_config[tabId]
    // Only return if there's actual content and NO sections (legacy mode)
    if (!tabData.sections && (tabData.content || tabData.title || tabData.subtitle)) {
      return tabData
    }
  }
  return null
}

// Get modern tab sections (new structure)
const getTabSections = (tabId: string) => {
  if (courseData.value?.tab_config && courseData.value.tab_config[tabId]) {
    const tabData = courseData.value.tab_config[tabId]
    // Return sections if they exist
    if (tabData.sections && Array.isArray(tabData.sections) && tabData.sections.length > 0) {
      return tabData.sections
    }
  }
  return null
}

// Get nested tabs structure (for Overview tab with horizontal nested tabs)
const getTabNestedTabs = (tabId: string) => {
  if (courseData.value?.tab_config && courseData.value.tab_config[tabId]) {
    const tabData = courseData.value.tab_config[tabId]
    // Return if it's a nested tabs type structure
    if (tabData.type === 'tabs' && tabData.tabs && Array.isArray(tabData.tabs) && tabData.tabs.length > 0) {
      return tabData
    }
  }
  return null
}

// Get vertical sections (for Overview tab with simple vertical layout - NO TABS)
const getTabVerticalSections = (tabId: string) => {
  if (courseData.value?.tab_config && courseData.value.tab_config[tabId]) {
    const tabData = courseData.value.tab_config[tabId]
    // Return if it's a vertical sections type structure
    if (tabData.type === 'vertical-sections' && tabData.sections && Array.isArray(tabData.sections) && tabData.sections.length > 0) {
      return tabData.sections
    }
  }
  return null
}

// Get projects array (for Projects tab with card grid layout)
const getTabProjects = (tabId: string) => {
  if (courseData.value?.tab_config && courseData.value.tab_config[tabId]) {
    const tabData = courseData.value.tab_config[tabId]
    // Return if it's a projects type structure
    if (tabData.type === 'projects' && tabData.projects && Array.isArray(tabData.projects) && tabData.projects.length > 0) {
      return tabData.projects
    }
  }
  return null
}

// Handle project card click
const handleProjectClick = (project: any) => {
  console.log('Project clicked:', project)
  // TODO: Handle project navigation or modal opening
  // You can add navigation logic here
}

// Get schedule data (for Schedule tab with flexibility layout)
const getTabSchedule = (tabId: string) => {
  if (courseData.value?.tab_config && courseData.value.tab_config[tabId]) {
    const tabData = courseData.value.tab_config[tabId]
    // Return if it's a schedule type structure
    if (tabData.type === 'schedule' && tabData.learningModes && Array.isArray(tabData.learningModes)) {
      return tabData
    }
  }
  return null
}

// Get FAQ data from tab_config
const getTabFAQs = (tabId: string) => {
  if (courseData.value?.tab_config && courseData.value.tab_config[tabId]) {
    const tabData = courseData.value.tab_config[tabId]
    // Return FAQs if they exist
    if (tabData.type === 'faqs' && tabData.faqs && Array.isArray(tabData.faqs)) {
      return tabData.faqs
    }
  }
  return null
}

// Get curriculum data - prioritize API data (mdl_course_curriculum), fallback to tab_config
const getTabCurriculum = (tabId: string) => {
  console.log('📚 getTabCurriculum called with:', tabId)

  // Priority 1: Use curriculum from API (mdl_course_curriculum table)
  if (curriculumModules.value && curriculumModules.value.length > 0) {
    console.log('📚 ✅ Using API curriculum:', curriculumModules.value.length, 'modules')
    // Initialize expanded state if needed
    if (curriculumExpanded.value.length !== curriculumModules.value.length) {
      curriculumExpanded.value = curriculumModules.value.map((_, index) => index === 0)
    }
    return curriculumModules.value
  }

  // Priority 2: Fallback to tab_config (legacy)
  console.log('📚 Checking tab_config fallback...')
  if (courseData.value?.tab_config && courseData.value.tab_config[tabId]) {
    const tabData = courseData.value.tab_config[tabId]

    // Return modules if it's a curriculum type
    if (tabData.type === 'curriculum' && tabData.modules && Array.isArray(tabData.modules)) {
      console.log('📚 ✅ Using tab_config curriculum:', tabData.modules.length, 'modules')
      // Initialize expanded state if needed
      if (curriculumExpanded.value.length !== tabData.modules.length) {
        curriculumExpanded.value = tabData.modules.map((_, index) => index === 0)
      }
      return tabData.modules
    }
  }
  console.log('📚 ❌ No curriculum found')
  return null
}

// Toggle curriculum module expansion
function toggleCurriculumModule(index: number) {
  curriculumExpanded.value[index] = !curriculumExpanded.value[index]
}

// Get course summary text - strip HTML and use from DB, fallback to default
function getCourseSummaryText(): string {
  if (courseData.value?.summary) {
    // Strip HTML tags and get plain text, limit to ~200 chars for hero section
    const plainText = courseData.value.summary.replace(/<[^>]*>/g, '').trim()
    if (plainText.length > 200) {
      return plainText.substring(0, 200).trim() + '...'
    }
    return plainText || 'Learn from industry experts with hands-on projects and real-world applications. Build job-ready skills and earn a certificate to showcase your expertise.'
  }
  return 'Learn from industry experts with hands-on projects and real-world applications. Build job-ready skills and earn a certificate to showcase your expertise.'
}

// Get icon for lesson type
function getLessonIcon(type: string): string {
  const icons: Record<string, string> = {
    lesson: 'fas fa-book-open',
    assignment: 'fas fa-clipboard-check',
    quiz: 'fas fa-question-circle',
    video: 'fas fa-video',
    project: 'fas fa-project-diagram',
    reading: 'fas fa-file-alt'
  }
  return icons[type] || 'fas fa-file-alt'
}

// Get display name for lesson type
function getLessonTypeName(type: string): string {
  const names: Record<string, string> = {
    lesson: 'Lesson',
    assignment: 'Assignment',
    quiz: 'Quiz',
    video: 'Video',
    project: 'Project',
    reading: 'Reading'
  }
  return names[type] || 'Lesson'
}

const learningObjectives = [
  {
    icon: 'fas fa-brain',
    title: 'Master AI Fundamentals',
    description: 'Understand core concepts of artificial intelligence and machine learning'
  },
  {
    icon: 'fas fa-code',
    title: 'Hands-On Coding',
    description: 'Build real AI projects using Python and modern frameworks'
  },
  {
    icon: 'fas fa-robot',
    title: 'Create AI Models',
    description: 'Train and deploy your own machine learning models'
  },
  {
    icon: 'fas fa-chart-line',
    title: 'Data Analysis',
    description: 'Learn to analyze and visualize data like a professional'
  },
  {
    icon: 'fas fa-lightbulb',
    title: 'Problem Solving',
    description: 'Develop critical thinking and computational skills'
  },
  {
    icon: 'fas fa-certificate',
    title: 'Get Certified',
    description: 'Earn an industry-recognized certificate upon completion'
  }
]

// Computed
const progressCircumference = computed(() => 2 * Math.PI * 85)
const progressOffset = computed(() => {
  const percentage = progress.value.overall
  return progressCircumference.value - (percentage / 100) * progressCircumference.value
})

const nextLevelXP = computed(() => progress.value.current_level * 1000)
const xpProgressPercentage = computed(() => {
  const currentLevelBase = (progress.value.current_level - 1) * 1000
  const xpInCurrentLevel = progress.value.xp_points - currentLevelBase
  return Math.min((xpInCurrentLevel / 1000) * 100, 100)
})

// Get keyHighlights from schedule tab_config for "Why you choose this course" sidebar
const sidebarKeyHighlights = computed(() => {
  if (courseData.value?.tab_config?.schedule?.keyHighlights) {
    return courseData.value.tab_config.schedule.keyHighlights
  }
  // Fallback to default highlights if not available
  return [
    { icon: 'fas fa-certificate', title: 'Certificate', description: 'Industry-recognized certification' },
    { icon: 'fas fa-headset', title: '24/7 Support', description: 'Get help anytime you need' },
    { icon: 'fas fa-shield-alt', title: '30-Day Guarantee', description: 'Money-back guarantee' },
    { icon: 'fas fa-lock', title: 'Secure Payment', description: 'Safe & encrypted transactions' }
  ]
})

// Methods
// Particle style generator for loading animation
function getParticleStyle(index: number) {
  const size = Math.random() * 6 + 2
  const duration = Math.random() * 3 + 2
  const delay = Math.random() * 2
  const left = Math.random() * 100
  const top = Math.random() * 100

  return {
    width: `${size}px`,
    height: `${size}px`,
    left: `${left}%`,
    top: `${top}%`,
    animationDuration: `${duration}s`,
    animationDelay: `${delay}s`
  }
}

// Star field animation styles for premium loader
function getStarStyle(index: number) {
  const size = Math.random() * 3 + 1
  const duration = Math.random() * 4 + 2
  const delay = Math.random() * 3
  const left = Math.random() * 100
  const top = Math.random() * 100
  const opacity = Math.random() * 0.7 + 0.3

  return {
    width: `${size}px`,
    height: `${size}px`,
    left: `${left}%`,
    top: `${top}%`,
    opacity: opacity,
    animationDuration: `${duration}s`,
    animationDelay: `${delay}s`
  }
}

// Cycle through loading messages
function startLoadingMessages() {
  let index = 0
  loadingMessageInterval = window.setInterval(() => {
    index = (index + 1) % loadingMessages.length
    loadingMessage.value = loadingMessages[index]
  }, 1500) // Change message every 1.5 seconds
}

function stopLoadingMessages() {
  if (loadingMessageInterval) {
    clearInterval(loadingMessageInterval)
    loadingMessageInterval = null
  }
}

function toggleSection(index: number) {
  sections.value[index].expanded = !sections.value[index].expanded
}

function countByType(activities: any[], types: string[]): number {
  if (!activities) return 0
  return activities.filter(activity => types.includes(activity.modname)).length
}

function getActivityIcon(modname: string): string {
  const icons: Record<string, string> = {
    page: 'fas fa-file-alt',
    resource: 'fas fa-file-download',
    url: 'fas fa-link',
    forum: 'fas fa-comments',
    quiz: 'fas fa-question-circle',
    assign: 'fas fa-pen-square',
    lesson: 'fas fa-book-open',
    label: 'fas fa-tag',
    video: 'fas fa-video'
  }
  return icons[modname] || 'fas fa-file'
}

function getActivityTypeName(modname: string): string {
  const names: Record<string, string> = {
    page: 'Reading',
    resource: 'Resource',
    url: 'Web Link',
    forum: 'Discussion',
    quiz: 'Quiz',
    assign: 'Assignment',
    lesson: 'Lesson',
    label: 'Info',
    video: 'Video'
  }
  return names[modname] || 'Activity'
}

function handleEnroll() {
  if (courseData.value) {
    // Redirect to checkout page with course details
    window.location.href = `/checkout.php?course_id=${courseData.value.id}&course=${encodeURIComponent(courseData.value.fullname)}`
  }
}

// Strip HTML tags for safe display
function stripHtmlTags(html: string): string {
  if (!html) return ''
  const tmp = document.createElement('DIV')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

// Retry loading on error
async function retryLoading() {
  error.value = null
  loading.value = true

  // Re-fetch course data
  const course = await fetchCourse(props.courseId)
  if (course) {
    courseData.value = course
    metadata.value = getCourseMetadata(course)
  } else {
    error.value = 'Failed to load course. Please check your connection and try again.'
  }

  loading.value = false
}

// Demo/Fallback data
const getDemoSections = () => [
  {
    id: 1,
    name: 'Introduction to AI Basics',
    summary: '<p>Get started with the fundamentals of artificial intelligence and machine learning.</p>',
    visible: 1,
    section: 0,
    activities: [
      { id: 1, name: 'Welcome to AI Basics', modname: 'page', description: '', url: '#', completion: 1 },
      { id: 2, name: 'Course Overview Video', modname: 'page', description: '', url: '#', completion: 1 },
      { id: 3, name: 'AI Fundamentals Quiz', modname: 'quiz', description: '', url: '#', completion: 0 }
    ]
  },
  {
    id: 2,
    name: 'Machine Learning Fundamentals',
    summary: '<p>Learn the core concepts of machine learning and how AI systems learn from data.</p>',
    visible: 1,
    section: 1,
    activities: [
      { id: 4, name: 'What is Machine Learning?', modname: 'page', description: '', url: '#', completion: 1 },
      { id: 5, name: 'Types of Machine Learning', modname: 'page', description: '', url: '#', completion: 0 },
      { id: 6, name: 'ML Algorithms Overview', modname: 'resource', description: '', url: '#', completion: 0 },
      { id: 7, name: 'ML Quiz 1', modname: 'quiz', description: '', url: '#', completion: 0 }
    ]
  },
  {
    id: 3,
    name: 'Building Your First AI Model',
    summary: '<p>Hands-on project to create your first working AI model.</p>',
    visible: 1,
    section: 2,
    activities: [
      { id: 8, name: 'Setting Up Your Environment', modname: 'page', description: '', url: '#', completion: 0 },
      { id: 9, name: 'Data Collection & Preparation', modname: 'page', description: '', url: '#', completion: 0 },
      { id: 10, name: 'Training Your Model', modname: 'page', description: '', url: '#', completion: 0 },
      { id: 11, name: 'Model Testing & Evaluation', modname: 'page', description: '', url: '#', completion: 0 },
      { id: 12, name: 'First AI Model Assignment', modname: 'assign', description: '', url: '#', completion: 0 }
    ]
  },
  {
    id: 4,
    name: 'Neural Networks & Deep Learning',
    summary: '<p>Explore advanced AI concepts with neural networks and deep learning.</p>',
    visible: 1,
    section: 3,
    activities: [
      { id: 13, name: 'Introduction to Neural Networks', modname: 'page', description: '', url: '#', completion: 0 },
      { id: 14, name: 'Deep Learning Explained', modname: 'page', description: '', url: '#', completion: 0 },
      { id: 15, name: 'Building a Neural Network', modname: 'page', description: '', url: '#', completion: 0 },
      { id: 16, name: 'Deep Learning Quiz', modname: 'quiz', description: '', url: '#', completion: 0 }
    ]
  },
  {
    id: 5,
    name: 'Final Project & Certification',
    summary: '<p>Apply everything you\'ve learned in a comprehensive final project.</p>',
    visible: 1,
    section: 4,
    activities: [
      { id: 17, name: 'Final Project Brief', modname: 'page', description: '', url: '#', completion: 0 },
      { id: 18, name: 'Project Guidelines', modname: 'resource', description: '', url: '#', completion: 0 },
      { id: 19, name: 'Submit Final Project', modname: 'assign', description: '', url: '#', completion: 0 },
      { id: 20, name: 'Course Completion Certificate', modname: 'page', description: '', url: '#', completion: 0 }
    ]
  }
]

// Function to hide the initial PHP loader
function hideInitialLoader() {
  const initialLoader = document.getElementById('initial-page-loader')
  if (initialLoader) {
    initialLoader.classList.add('fade-out')
    setTimeout(() => {
      initialLoader.classList.add('hidden')
    }, 400)
    console.log('🔄 Initial PHP loader hidden, Vue loader active')
  }
}

// Lifecycle
onMounted(async () => {
  console.log('🎯 DynamicCoursePage mounted with courseId:', props.courseId)

  // IMPORTANT: Set loading to false to prevent Vue's loading screen from showing
  // The PHP initial loader handles the initial loading experience
  // We hide it only after data is loaded to prevent double loading
  loading.value = false

  // Fetch course data
  const course = await fetchCourse(props.courseId)
  if (course) {
    console.log('✅ Course data loaded:', course.fullname)
    courseData.value = course
    metadata.value = getCourseMetadata(course)

    // Set active tab from course configuration
    if (course.tab_config?.default_tab) {
      activeTab.value = course.tab_config.default_tab
      console.log('📑 Using default tab from config:', course.tab_config.default_tab)
    }
  } else {
    console.log('⚠️ Using fallback course data')
    // Fallback course data
    courseData.value = {
      id: props.courseId,
      fullname: `Course #${props.courseId}`,
      shortname: 'AI Basics',
      summary: 'Comprehensive AI course for all levels',
      category: 1,
      category_name: 'AI Courses',
      format: 'topics',
      startdate: Date.now(),
      enddate: 0,
      visible: 1,
      enablecompletion: 1,
      is_enrolled: false,
      completion_percentage: 0
    }
  }

  // Fetch curriculum from API (mdl_course_curriculum table)
  try {
    const curriculumResponse = await fetch(`/api/v1/academy/courses/${props.courseId}/curriculum`)
    if (curriculumResponse.ok) {
      const curriculumData = await curriculumResponse.json()
      if (Array.isArray(curriculumData) && curriculumData.length > 0) {
        curriculumModules.value = curriculumData
        console.log('📚 Loaded curriculum from API:', curriculumData.length, 'modules')
      }
    }
  } catch (e) {
    console.log('⚠️ Could not fetch curriculum from API, will use tab_config fallback')
  }

  // Check if reviews exist for this course (to show/hide Reviews tab)
  try {
    const reviewsUrl = props.courseId > 0
      ? `/api/v1/academy/reviews?course_id=${props.courseId}&limit=1`
      : '/api/v1/academy/reviews?limit=1'
    const reviewsResponse = await fetch(reviewsUrl)
    if (reviewsResponse.ok) {
      const reviewsData = await reviewsResponse.json()
      // Only check the reviews array length - aggregate counts ALL reviews globally
      hasReviews.value = (reviewsData.reviews?.length || 0) > 0
      console.log('⭐ Reviews check:', hasReviews.value ? 'Reviews exist' : 'No reviews')
    }
  } catch (e) {
    console.log('⚠️ Could not check reviews, hiding reviews tab')
    hasReviews.value = false
  }

  // Fetch sections - with fallback to demo data
  const courseSections = await fetchCourseSections(props.courseId)
  console.log('📚 Fetched sections:', courseSections.length)

  const sectionsData = courseSections.length > 0 ? courseSections : getDemoSections()
  console.log('📋 Using sections data (including fallback):', sectionsData.length)

  sections.value = sectionsData.map((section, index) => {
    // Calculate actual progress based on activities completion
    const totalActivities = section.activities?.length || 0
    const completedActivities = section.activities?.filter(a => a.completion === 1).length || 0
    const sectionProgress = totalActivities > 0 ? Math.floor((completedActivities / totalActivities) * 100) : 0

    // Determine status based on progress
    let sectionStatus = 'locked'
    if (sectionProgress > 0 && sectionProgress < 100) {
      sectionStatus = 'in-progress'
    } else if (sectionProgress === 100) {
      sectionStatus = 'completed'
    } else if (index === 0) {
      sectionStatus = 'in-progress'
    }

    return {
      ...section,
      expanded: index === 0,
      progress: sectionProgress,
      status: sectionStatus
    }
  })

  console.log('✅ Sections processed:', sections.value.length)

  // Calculate progress
  progress.value = calculateProgress(sectionsData)
  console.log('📊 Progress calculated:', progress.value.overall + '%')

  // Hide the initial PHP loader NOW that all data is loaded
  // This ensures smooth transition from PHP loader to Vue content
  hideInitialLoader()

  console.log('🎉 DynamicCoursePage setup complete!')
})
</script>

<script lang="ts">
export default {
  name: 'DynamicCoursePage'
}
</script>

<style src="../../assets/dynamic-course.css"></style>
<style src="../../assets/elite-platform-features.css"></style>
